import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, addDoc, Timestamp, getDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged, User, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { Question, Category, Feedback, UserResult, AppConfig } from '../types';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight, Check, ExternalLink } from 'lucide-react';
import { cn } from '../lib/utils';

interface AssessmentProps {
  onClose: () => void;
  onComplete: (scores: { responsibility: number; selftrust: number; care: number }) => void;
}

const CAT_LABEL: Record<Category, string> = { 
  responsibility: 'Responsibility', 
  selftrust: 'Self Trust', 
  care: 'Care', 
  overall: 'Overall',
  shadow_responsibility: 'Shadow Responsibility',
  shadow_selftrust: 'Shadow Self Trust',
  shadow_care: 'Shadow Care'
};
const CAT_BADGE: Record<Category, string> = { 
  responsibility: 'bg-cat-responsibility/15 text-cat-responsibility', 
  selftrust: 'bg-cat-selftrust/15 text-cat-selftrust', 
  care: 'bg-cat-care/15 text-cat-care', 
  overall: 'bg-text-main/15 text-text-main',
  shadow_responsibility: 'bg-cat-responsibility/15 text-cat-responsibility',
  shadow_selftrust: 'bg-cat-selftrust/15 text-cat-selftrust',
  shadow_care: 'bg-cat-care/15 text-cat-care'
};
const CAT_SEL: Record<Category, string> = { 
  responsibility: 'bg-cat-responsibility/20 border-cat-responsibility text-cat-responsibility', 
  selftrust: 'bg-cat-selftrust/20 border-cat-selftrust text-cat-selftrust', 
  care: 'bg-cat-care/20 border-cat-care text-cat-care', 
  overall: 'bg-text-main/20 border-text-main text-text-main',
  shadow_responsibility: 'bg-cat-responsibility/20 border-cat-responsibility text-cat-responsibility',
  shadow_selftrust: 'bg-cat-selftrust/20 border-cat-selftrust text-cat-selftrust',
  shadow_care: 'bg-cat-care/20 border-cat-care text-cat-care'
};

export const Assessment: React.FC<AssessmentProps> = ({ onClose, onComplete }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [categoryDescriptions, setCategoryDescriptions] = useState<Record<Category, string>>({ 
    responsibility: '', selftrust: '', care: '', overall: '',
    shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
  });
  const [categoryShortDescriptions, setCategoryShortDescriptions] = useState<Record<Category, string>>({ 
    responsibility: '', selftrust: '', care: '', overall: '',
    shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
  });
  const [finalScores, setFinalScores] = useState({ responsibility: 0, selftrust: 0, care: 0 });
  const [user, setUser] = useState<User | null>(auth.currentUser);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const qSnap = await getDocs(query(collection(db, 'questions'), orderBy('order')));
        const qs = qSnap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Question));
        setQuestions(qs);

        const fSnap = await getDocs(collection(db, 'feedback'));
        const fs = fSnap.docs.map(doc => ({ ...doc.data(), id: doc.id } as Feedback));
        setFeedback(fs);

        const cSnap = await getDoc(doc(db, 'config', 'app'));
        if (cSnap.exists()) {
          const data = cSnap.data() as AppConfig;
          setCategoryDescriptions((data.categoryDescriptions || { 
            responsibility: '', selftrust: '', care: '', overall: '',
            shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
          }) as Record<Category, string>);
          setCategoryShortDescriptions((data.categoryShortDescriptions || { 
            responsibility: '', selftrust: '', care: '', overall: '',
            shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
          }) as Record<Category, string>);
        }
      } catch (error) {
        console.error("Error fetching assessment data:", error);
        // It's either questions or feedback that failed
        handleFirestoreError(error, OperationType.LIST, 'questions');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSelect = (n: number) => {
    const q = questions[currentIdx];
    setAnswers(prev => ({ ...prev, [q.id]: n }));
    
    // Auto-advance if not the last question
    if (currentIdx < questions.length - 1) {
      setTimeout(() => {
        setCurrentIdx(prev => prev + 1);
      }, 250);
    }
  };

  const next = () => {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(prev => prev + 1);
    }
  };

  const prev = () => {
    if (currentIdx > 0) {
      setCurrentIdx(prev => prev - 1);
    }
  };

  const submit = async () => {
    const sums = { responsibility: 0, selftrust: 0, care: 0 };
    const counts = { responsibility: 0, selftrust: 0, care: 0 };
    const shadowSums = { responsibility: 0, selftrust: 0, care: 0 };
    const shadowCounts = { responsibility: 0, selftrust: 0, care: 0 };

    questions.forEach(q => {
      const ans = answers[q.id];
      if (ans != null) {
        const isShadow = q.isShadow || q.category.startsWith('shadow_');
        const baseCat = (q.category.startsWith('shadow_') ? q.category.replace('shadow_', '') : q.category) as keyof typeof sums;

        if (!isShadow) {
          sums[baseCat] += ans;
          counts[baseCat] += 1;
        } else {
          shadowSums[baseCat] += ans;
          shadowCounts[baseCat] += 1;
        }
      }
    });

    const scores = {
      responsibility: counts.responsibility > 0 ? sums.responsibility / counts.responsibility : 0,
      selftrust: counts.selftrust > 0 ? sums.selftrust / counts.selftrust : 0,
      care: counts.care > 0 ? sums.care / counts.care : 0,
    };

    const shadowScores = {
      responsibility: shadowCounts.responsibility > 0 ? shadowSums.responsibility / shadowCounts.responsibility : 0,
      selftrust: shadowCounts.selftrust > 0 ? shadowSums.selftrust / shadowCounts.selftrust : 0,
      care: shadowCounts.care > 0 ? shadowSums.care / shadowCounts.care : 0,
    };

    setFinalScores(scores);
    setCompleted(true);
    onComplete(scores);

    if (auth.currentUser) {
      try {
        const resultData = {
          userId: auth.currentUser.uid,
          scores,
          shadowScores,
          answers: questions.map(q => ({
            questionId: q.id,
            score: answers[q.id] || 0,
            category: q.category
          })),
          timestamp: Timestamp.now(),
        };
        if (import.meta.env.DEV) console.log("Saving result to Firestore:", resultData);
        const docRef = await addDoc(collection(db, 'users', auth.currentUser.uid, 'results'), resultData);
        if (import.meta.env.DEV) console.log("Result saved with ID:", docRef.id);

        // Notify admin via API
        // try {
        //   const response = await fetch('/api/notify-admin', {
        //     method: 'POST',
        //     headers: {
        //       'Content-Type': 'application/json',
        //     },
        //     body: JSON.stringify({
        //       name: auth.currentUser.displayName || auth.currentUser.email || 'Anonymous User',
        //       email: auth.currentUser.email || 'N/A',
        //       scores,
        //     }),
        //   });
        //   const resData = await response.json();
        //   if (import.meta.env.DEV) {
        //     console.log("Admin notification response:", resData);
        //   }
        // } catch (mailErr) {
        //   console.error("Failed to make active admin notification request:", mailErr);
        // }
      } catch (error) {
        console.error("Error saving results:", error);
        handleFirestoreError(error, OperationType.CREATE, `users/${auth.currentUser.uid}/results`);
      }
    }
  };

  const login = () => signInWithPopup(auth, new GoogleAuthProvider());

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay backdrop-blur-sm">
        <div className="text-text-muted animate-pulse uppercase tracking-[0.3em] text-xs">Loading Assessment...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay backdrop-blur-sm p-6">
        <div className="bg-bg-card border border-border-secondary rounded-2xl p-8 max-w-md w-full text-center space-y-6">
          <p className="text-text-main text-lg font-light tracking-widest uppercase">Authentication Required</p>
          <p className="text-text-secondary text-sm leading-relaxed">Please sign in to take the assessment and track your progress.</p>
          <div className="flex flex-col gap-3">
            <button onClick={login} className="w-full py-3 bg-bg-input border border-border-main rounded-xl text-text-main hover:bg-bg-card transition-all tracking-widest text-xs uppercase">Sign in with Google</button>
            <button onClick={onClose} className="w-full py-3 bg-bg-main border border-border-secondary rounded-xl text-text-muted hover:text-text-secondary transition-all tracking-widest text-xs uppercase">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay backdrop-blur-sm p-6">
        <div className="bg-bg-card border border-border-secondary rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-text-muted mb-6">No questions have been added yet.</p>
          <button onClick={onClose} className="px-6 py-2 bg-bg-main border border-border-secondary rounded-lg text-text-secondary hover:text-text-main hover:border-border-focus transition-all">Close</button>
        </div>
      </div>
    );
  }

  const q = questions[currentIdx];
  const selected = answers[q.id];
  const progress = ((currentIdx + 1) / questions.length) * 100;

  if (completed) {
    const total = finalScores.responsibility + finalScores.selftrust + finalScores.care;
    const overallScore = total / 3;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay backdrop-blur-sm p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-bg-card border border-border-secondary rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-border-main flex justify-between items-center">
            <span className="text-[0.68rem] tracking-[0.25em] uppercase text-text-muted">Your Results</span>
            <button onClick={onClose} className="text-text-muted hover:text-text-main transition-colors"><X size={20} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-8 space-y-8">
            <div className="text-center space-y-6">
              <div className="flex justify-center gap-8 flex-wrap">
                {(['responsibility', 'selftrust', 'care'] as Category[]).map(cat => (
                  <div key={cat} className="flex flex-col gap-1">
                    <span className={cn("text-[0.62rem] tracking-[0.18em] uppercase", cat === 'responsibility' ? 'text-cat-responsibility' : cat === 'selftrust' ? 'text-cat-selftrust' : 'text-cat-care')}>{CAT_LABEL[cat]}</span>
                    <span className={cn("text-3xl font-extralight", cat === 'responsibility' ? 'text-cat-responsibility' : cat === 'selftrust' ? 'text-cat-selftrust' : 'text-cat-care')}>{finalScores[cat].toFixed(1)}</span>
                  </div>
                ))}
                <div className="flex flex-col gap-1 border-l border-border-main pl-8">
                  <span className="text-[0.62rem] tracking-[0.18em] uppercase text-text-main">Overall Agency</span>
                  <span className="text-3xl font-extralight text-cat-overall">{total.toFixed(1)}</span>
                </div>
              </div>
              
              <div className="pt-4 border-t border-border-main max-w-md mx-auto">
                <p className="text-text-muted text-[0.65rem] leading-relaxed italic">
                  Note: To ensure meaningful reflection, you are limited to one assessment per day. 
                  Your results are saved and can be explored in detail on your dashboard.
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 border-t border-border-main flex justify-center">
            <button onClick={onClose} className="px-8 py-3 bg-bg-main border border-border-secondary rounded-xl text-text-secondary hover:text-text-main hover:border-border-focus transition-all uppercase tracking-widest text-xs">Close & Explore</button>
          </div>
        </motion.div>
      </div>
    );
  }

  const getDisplayCategory = (cat: Category) => {
    if (cat.startsWith('shadow_')) return cat.replace('shadow_', '') as Category;
    return cat;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay backdrop-blur-sm p-6">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-bg-card border border-border-secondary rounded-2xl w-full max-w-xl flex flex-col overflow-hidden shadow-2xl">
        <div className="p-5 border-b border-border-main flex justify-between items-center">
          <span className="text-[0.68rem] tracking-[0.25em] uppercase text-text-muted">Assessment — {CAT_LABEL[getDisplayCategory(q.category)]}</span>
          <button onClick={onClose} className="text-text-muted hover:text-text-main transition-colors"><X size={20} /></button>
        </div>
        <div className="h-0.5 bg-bg-main w-full">
          <motion.div className="h-full bg-gradient-to-r from-cat-responsibility via-cat-selftrust to-cat-care" initial={{ width: 0 }} animate={{ width: `${progress}%` }} />
        </div>
        <div className="p-8 space-y-8">
          <div className="space-y-4">
            <div className="space-y-2">
              <span className={cn("text-[0.62rem] tracking-[0.2em] uppercase font-semibold px-3 py-1 rounded-[5px]", CAT_BADGE[getDisplayCategory(q.category)])}>{CAT_LABEL[getDisplayCategory(q.category)]}</span>
              <p className="text-lg font-light leading-relaxed text-text-main">{q.text}</p>
            </div>
            {q.description && (
              <p className="text-sm leading-relaxed text-text-muted border-l-2 border-border-main pl-4 py-1 italic">
                {q.description}
              </p>
            )}
          </div>
          <div className="space-y-4">
            <p className="text-[0.65rem] tracking-[0.15em] text-text-muted uppercase">How true is this for you?</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => handleSelect(n)} className={cn("flex-1 aspect-square max-w-[60px] rounded-lg border border-border-secondary bg-bg-main text-text-secondary text-lg transition-all flex items-center justify-center hover:border-border-focus hover:text-text-main", selected === n && CAT_SEL[getDisplayCategory(q.category)])}>
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between tracking-wider px-1">
              <span className="text-[12px] text-text-muted">Not at all</span>
              <span className="text-[11px] text-text-muted">Very much</span>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-border-main flex items-center justify-between">
          <span className="text-[11px] text-text-muted tracking-widest">{currentIdx + 1} of {questions.length}</span>
          <div className="flex gap-2">
            <button onClick={prev} disabled={currentIdx === 0} className="px-5 py-2.5 bg-bg-main border border-border-secondary rounded-lg text-text-secondary hover:text-text-main hover:border-border-focus transition-all disabled:opacity-30 text-[12px] tracking-wider">← Back</button>
            {currentIdx === questions.length - 1 && (
              <button onClick={submit} disabled={selected == null} className="px-6 py-2.5 bg-bg-input border border-border-main rounded-lg text-text-secondary hover:bg-bg-card hover:text-text-main transition-all disabled:opacity-30 text-[12px] tracking-widest uppercase">Submit</button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
